import { useQuery } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Alert, Badge, Button, Card, Field, Select, Spinner } from '../../components/ui';
import { type MessageKey, t } from '../../i18n';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { errorMessage } from '../../lib/errors';
import { can } from '../../lib/permissions';
import { formatDateTime, natureName, type Scope, statusTone, type TicketListItem } from './data';

const PAGE = 50;
const FILTERS = ['abiertos', 'en_cola', 'en_curso', 'terminado', 'cancelado', 'todos'] as const;

/** Lista de tickets (guía 7.1): los propios para todos; los asignados y los de la organización para IT. */
export function TicketsPage() {
  const me = useMe();
  const manages = can(me, 'tickets.gestionar');
  const sees = manages || can(me, 'tickets.ver');
  const scopes: Scope[] = ['propios', ...(manages ? (['asignados'] as const) : []), ...(sees ? (['todos'] as const) : [])];

  const [params, setParams] = useSearchParams();
  const requested = params.get('alcance') as Scope | null;
  const scope: Scope = requested && scopes.includes(requested) ? requested : manages ? 'asignados' : 'propios';
  const filter = (FILTERS as readonly string[]).includes(params.get('estado') ?? '') ? params.get('estado')! : 'abiertos';
  const search = params.get('buscar') ?? '';
  const [draft, setDraft] = useState(search);
  const [limit, setLimit] = useState(PAGE);

  const query = new URLSearchParams({ alcance: scope, limite: String(limit) });
  if (filter !== 'todos') query.set('estado', filter);
  if (search) query.set('buscar', search);
  const tickets = useQuery({
    queryKey: ['tickets', 'lista', scope, filter, search, limit],
    queryFn: () => api<TicketListItem[]>(`/api/tickets?${query}`),
    placeholderData: (prev) => prev,
  });

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(changes)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setLimit(PAGE);
    setParams(next, { replace: true });
  };
  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    update({ buscar: draft.trim() || null });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-tinta">{t('tk.titulo')}</h1>
          <p className="max-w-2xl text-sm text-slate-600">{t('tk.descripcion')}</p>
        </div>
        <Link to="/tickets/nuevo" className="rounded bg-marca px-4 py-2 text-sm font-medium text-white hover:bg-marca-claro">
          {t('tk.nuevo')}
        </Link>
      </div>

      {scopes.length > 1 && (
        <div role="tablist" aria-label={t('tk.alcance')} className="flex gap-1 border-b border-slate-200">
          {scopes.map((s) => (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={s === scope}
              className={`-mb-px border-b-2 px-3 py-2 text-sm ${s === scope ? 'border-marca font-medium text-slate-900' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
              onClick={() => update({ alcance: s })}
            >
              {t(`tk.alcance_${s}` as MessageKey)}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-48">
          <Select id="tk-estado" label={t('tk.estado')} value={filter} onChange={(e) => update({ estado: e.target.value === 'abiertos' ? null : e.target.value })}>
            {FILTERS.map((f) => (
              <option key={f} value={f}>
                {t(`tk.filtro_${f}` as MessageKey)}
              </option>
            ))}
          </Select>
        </div>
        <form onSubmit={submitSearch} className="flex items-end gap-2" role="search">
          <div className="w-72">
            <Field id="tk-buscar" type="search" label={t('tk.buscar')} placeholder={t('tk.buscar_ayuda')} value={draft} onChange={(e) => setDraft(e.target.value)} />
          </div>
          <Button type="submit" variant="secondary">
            {t('tk.buscar')}
          </Button>
        </form>
      </div>

      <Card className="p-0">
        {!tickets.data ? (
          <div className="p-5">{tickets.error ? <Alert>{errorMessage(tickets.error)}</Alert> : <Spinner label={t('comun.cargando')} />}</div>
        ) : tickets.data.length === 0 ? (
          <p className="p-5 text-sm text-slate-500">{search ? t('tk.sin_resultados') : t('tk.vacio')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-xs text-slate-500">
                <tr>
                  <th className="px-4 py-2 font-medium">{t('tk.col_ticket')}</th>
                  <th className="px-4 py-2 font-medium">{t('tk.col_estado')}</th>
                  <th className="px-4 py-2 font-medium">{t('tk.col_prioridad')}</th>
                  <th className="px-4 py-2 font-medium">{t('tk.col_vence')}</th>
                  <th className="px-4 py-2 font-medium">{t('tk.col_creado')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tickets.data.map((tk) => (
                  <tr key={tk.id} className="hover:bg-slate-50">
                    {/* El borde izquierdo lleva el color de la prioridad: lo urgente se ve sin leer. */}
                    <td className="border-l-[3px] px-4 py-2.5" style={{ borderLeftColor: tk.priorityColor ?? 'transparent' }}>
                      <Link to={`/tickets/${tk.number}`} className="font-medium text-slate-900 hover:underline">
                        <span className="mr-2 font-mono text-xs text-slate-500">{tk.code}</span>
                        {tk.title}
                      </Link>
                      <span className="block text-xs text-slate-500">{natureName(tk.nature)}</span>
                    </td>
                    <td className="px-4 py-2">
                      <Badge tone={statusTone(tk.statusCategory)}>{tk.status}</Badge>
                    </td>
                    <td className="px-4 py-2">
                      {tk.priority ? <PriorityLabel name={tk.priority} color={tk.priorityColor} /> : <span className="text-slate-400">{t('tk.sin_clasificar')}</span>}
                    </td>
                    <td className="px-4 py-2 text-slate-600">
                      {tk.slaPausedSince ? t('tk.sla_pausado') : tk.slaDueAt && (tk.statusCategory === 'en_cola' || tk.statusCategory === 'en_curso') ? <DueLabel iso={tk.slaDueAt} /> : '—'}
                    </td>
                    <td className="px-4 py-2 text-slate-600">{formatDateTime(tk.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {tickets.data?.length === limit && (
        <Button variant="secondary" disabled={tickets.isFetching} onClick={() => setLimit(limit + PAGE)}>
          {t('tk.ver_mas')}
        </Button>
      )}
    </div>
  );
}

export function PriorityLabel({ name, color }: { name: string; color: string | null }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color ?? '#94a3b8' }} aria-hidden />
      {name}
    </span>
  );
}

function DueLabel({ iso }: { iso: string }) {
  const overdue = new Date(iso).getTime() < Date.now();
  return <span className={overdue ? 'font-medium text-red-700' : ''}>{overdue ? t('tk.vencio', { fecha: formatDateTime(iso) }) : formatDateTime(iso)}</span>;
}
